import { LinkedError, checkAxiosResponse, handleAxiosError } from '@mst-fe/shared';
import axios from 'axios';

import { observeEndpoint } from '../../services/endpoint-observer';
import { axiosOptions } from '../../services/requests';
import { WbInstanceStatus } from '../instances/workbench-api';
import { UserData } from '../users/users-api';

export type WbServiceType = 'streamlit' | 'voila' | 'web-python' | 'r-shiny' | 'browser-app';

export type WbServiceStatus = 'PROVISIONING' | 'STOPPING' | 'RUNNING' | 'FAILED' | 'STOPPED' | 'REMOVING' | 'UNINSTALLING';

export type UserServerStatus = 'RUNNING' | 'STOPPED' | 'PROVISIONING' | 'STOPPING' | 'FAILED';

export interface WbServiceData {
  id: string;
  serverId: string;
  serverName: string;
  shortId: string;
  wbName: string;
  name: string;
  type: WbServiceType;
  status: WbServiceStatus;
  instanceStatus?: WbInstanceStatus;
  url: string;
  ownedByUser: boolean;
  isDefaultServer: boolean;
  isPublishedByYou: boolean;
  isSharedToOthers: boolean;
  isInstalledFromStore: boolean;
  publishedModuleId?: string;
  permissions: SharePermissions;
}

export interface Server {
  id: string;
  name: string;
  mem: number;
  cpu: number;
  status: UserServerStatus;
  buildLogsPath: string;
}

export type SharePermissions = { type: 'organization' } | { type: 'users'; userSubjects: string[] };

export class ServicesApi {
  async getUserServices(): Promise<WbServiceData[] | undefined> {
    return await checkAxiosResponse(axios.get<WbServiceData[]>(`/api/zoe/dashboard/user-services`, axiosOptions()));
  }

  getUserServicesSSE(cb: (value: WbServiceData[]) => void) {
    return observeEndpoint<WbServiceData[]>(`/api/zoe/dashboard/user-services/sse`, cb);
  }

  async getServers(wbShortId: string): Promise<Server[] | undefined> {
    return await checkAxiosResponse(axios.get<Server[]>(`/api/madcat/${wbShortId}/servers/`, axiosOptions()));
  }

  async getAuthorizedUsers(wbShortId: string): Promise<UserData[] | undefined> {
    return await checkAxiosResponse(axios.get<UserData[]>(`/api/madcat/${wbShortId}/users`, axiosOptions()));
  }

  async deployService(wbShortId: string, serviceId: string): Promise<{ ok: boolean; error: string } | undefined> {
    const responseOrError = await handleAxiosError(
      axios.post<{ ok: boolean; error: string }>(`/api/madcat/${wbShortId}/services/${serviceId}/deploy`, {}, axiosOptions())
    );
    if (responseOrError instanceof LinkedError) {
      if (responseOrError.cause.responseStatus === 400) {
        return responseOrError.cause.responseData as { ok: boolean; error: string };
      }

      return undefined;
    }

    return responseOrError;
  }

  async stopService(wbShortId: string, serviceId: string): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.post<{ ok: boolean }>(`/api/madcat/${wbShortId}/services/${serviceId}/stop`, {}, axiosOptions()));
  }

  async deleteService(wbShortId: string, serviceId: string): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(axios.delete<{ ok: boolean }>(`/api/madcat/${wbShortId}/services/${serviceId}`, axiosOptions()));
  }

  async uninstallService(serviceId: string): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(
      axios.post<{ ok: boolean }>(`/api/madcat/uninstall-browser-app/service/${serviceId}`, {}, axiosOptions())
    );
  }

  async shareService(wbShortId: string, serviceId: string, permissions: SharePermissions): Promise<{ ok: boolean } | undefined> {
    return await checkAxiosResponse(
      axios.post<{ ok: boolean }>(`/api/madcat/${wbShortId}/services/${serviceId}/permissions`, permissions, axiosOptions()),
      { errorMessage: 'Error while upserting permissions' }
    );
  }
}
